import React, { useState, useEffect, useCallback } from "react";
import { Select, Tag, Space, Tooltip, message, Button, Spin } from "antd";
import { TeamOutlined, MailOutlined, QuestionCircleOutlined, ClearOutlined } from "@ant-design/icons";
import { masterDataService } from "../../../api/enhancedApi";

// 通用分组信息 Tooltip 组件
// 负责在悬浮时显示分组详情（成员列表等），支持按需加载
const GroupInfoTooltip = ({ groupName, initialGroupInfo, children, placement }) => {
    const [groupDetail, setGroupDetail] = useState(initialGroupInfo);
    const [loading, setLoading] = useState(false);

    const handleOpenChange = async (open) => {
        // 如果展开且没有详细信息（比如成员列表），则去加载
        if (open && !groupDetail?.members && !loading && groupDetail?.id) {
            setLoading(true);
            try {
                // 调用后端接口获取分组成员
                const response = await masterDataService.request("/email-addresses", {
                    method: "GET",
                    params: { group_id: groupDetail.id, is_active: true },
                });
                if (response && response.items) {
                    setGroupDetail((prev) => ({ ...prev, members: response.items }));
                }
            } catch (error) {
                console.error("加载组成员失败", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const tooltip = (
        <div style={{ padding: 4, minWidth: 200, maxWidth: 350 }}>
            {/* 头部：分组信息 */}
            <div style={{ paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.2)", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, fontSize: "14px", display: "flex", alignItems: "center" }}>
                        <TeamOutlined style={{ marginRight: 6 }} />
                        {groupName}
                    </span>
                    {(groupDetail?.email_count !== undefined || groupDetail?.members) && <Tag style={{ margin: 0, fontSize: 10, lineHeight: "16px", background: "rgba(255,255,255,0.1)", border: "none", color: "inherit" }}>{groupDetail?.members?.length || groupDetail?.email_count || 0} 人</Tag>}
                </div>
                {groupDetail?.description && <div style={{ opacity: 0.75, fontSize: 12 }}>{groupDetail.description}</div>}
            </div>

            <div style={{ fontSize: 12 }}>
                {loading ? (
                    <div style={{ textAlign: "center", padding: "8px 0" }}>
                        <Spin size="small" /> 加载成员中...
                    </div>
                ) : groupDetail?.members?.length > 0 ? (
                    <div style={{ maxHeight: 250, overflowY: "auto" }}>
                        {groupDetail.members.map((member, i) => (
                            <div
                                key={member.id || i}
                                style={{
                                    padding: "4px 0",
                                    borderBottom: i < groupDetail.members.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>{member.display_name}</div>
                                <div style={{ opacity: 0.65 }}>{member.email}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ opacity: 0.5, textAlign: "center", padding: "8px 0" }}>{groupDetail?.id ? "该分组下暂无成员" : "无法加载成员信息"}</div>
                )}
            </div>
        </div>
    );

    return (
        <Tooltip title={tooltip} overlayStyle={{ maxWidth: 350 }} onOpenChange={handleOpenChange} placement={placement} mouseEnterDelay={1} destroyTooltipOnHide>
            {children}
        </Tooltip>
    );
};

// 全局缓存，避免重复请求
let emailGroupsCache = null;
let emailGroupsPromise = null;
let emailAddressesCache = null;
let emailAddressesPromise = null;

/**
 * 收件人选择器
 * 支持邮箱地址和邮箱分组的混合选择
 */
const RecipientSelector = ({ value = [], onChange, placeholder = "选择邮箱分组或邮箱地址，或输入邮箱地址", allowGroups = true, allowAddresses = true, allowCustomInput = true, disabled = false, allowClear = false, style = {}, refreshKey = 0 }) => {
    const [emailGroups, setEmailGroups] = useState([]);
    const [emailAddresses, setEmailAddresses] = useState([]);
    const [loading, setLoading] = useState(false);

    // 加载邮箱分组（使用缓存避免重复请求）
    const loadEmailGroups = useCallback(async () => {
        if (!allowGroups) return;

        // 如果有缓存，直接使用
        if (emailGroupsCache) {
            setEmailGroups(emailGroupsCache);
            return;
        }

        // 如果正在请求中，等待请求完成
        if (emailGroupsPromise) {
            try {
                const cachedGroups = await emailGroupsPromise;
                setEmailGroups(cachedGroups);
            } catch (error) {
                console.error("等待邮箱分组加载失败:", error);
            }
            return;
        }

        try {
            setLoading(true);
            emailGroupsPromise = masterDataService.emailGroup
                .getEmailGroupList({
                    is_active: true,
                    page: 1,
                    page_size: 100,
                })
                .then((response) => {
                    const groups = response.data.items || [];
                    emailGroupsCache = groups; // 缓存结果
                    emailGroupsPromise = null; // 清除 Promise
                    return groups;
                });

            const groups = await emailGroupsPromise;
            setEmailGroups(groups);
        } catch (error) {
            console.error("加载邮箱分组失败:", error);
            message.error("加载邮箱分组失败");
            emailGroupsPromise = null; // 出错时也要清除 Promise
        } finally {
            setLoading(false);
        }
    }, [allowGroups]);

    // 加载邮箱地址（使用缓存避免重复请求）
    const loadEmailAddresses = useCallback(async () => {
        if (!allowAddresses) return;

        // 如果有缓存，直接使用
        if (emailAddressesCache) {
            setEmailAddresses(emailAddressesCache);
            return;
        }

        // 如果正在请求中，等待请求完成
        if (emailAddressesPromise) {
            try {
                const cachedAddresses = await emailAddressesPromise;
                setEmailAddresses(cachedAddresses);
            } catch (error) {
                console.error("等待邮箱地址加载失败:", error);
            }
            return;
        }

        try {
            setLoading(true);
            emailAddressesPromise = masterDataService.emailAddress
                .getEmailAddressList({
                    is_active: true,
                    page: 1,
                    page_size: 200, // 获取更多邮箱地址
                })
                .then((response) => {
                    const addresses = response.data.items || [];
                    emailAddressesCache = addresses; // 缓存结果
                    emailAddressesPromise = null; // 清除 Promise
                    return addresses;
                });

            const addresses = await emailAddressesPromise;
            setEmailAddresses(addresses);
        } catch (error) {
            console.error("加载邮箱地址失败:", error);
            message.error("加载邮箱地址失败");
            emailAddressesPromise = null; // 出错时也要清除 Promise
        } finally {
            setLoading(false);
        }
    }, [allowAddresses]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // 如果收到刷新信号（refreshKey > 0），则清除缓存以强制重新加载
                if (refreshKey > 0) {
                    console.log("[RecipientSelector] 收到刷新信号，清除缓存重新加载");
                    emailGroupsCache = null;
                    emailGroupsPromise = null;
                    emailAddressesCache = null;
                    emailAddressesPromise = null;
                }

                await Promise.all([allowGroups ? loadEmailGroups() : Promise.resolve(), allowAddresses ? loadEmailAddresses() : Promise.resolve()]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [allowGroups, allowAddresses, refreshKey, loadEmailGroups, loadEmailAddresses]);

    // 验证邮箱地址格式
    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    // 判断是否为邮箱分组
    const isEmailGroup = useCallback(
        (item) => {
            return emailGroups.some((group) => group.name === item);
        },
        [emailGroups],
    );

    // 判断是否为已存在的邮箱地址
    const isExistingEmailAddress = useCallback(
        (item) => {
            return emailAddresses.some((addr) => addr.email === item);
        },
        [emailAddresses],
    );

    // 判断是否为邮箱地址
    const isEmailAddress = (item) => {
        if (!item || typeof item !== "string") return false;
        return item.includes("@") && validateEmail(item);
    };

    // 处理选择变化
    const handleChange = useCallback(
        (newValues, option) => {
            // 验证新添加的值
            const validatedValues = [];

            for (const val of newValues) {
                // 确保val是有效的字符串
                if (!val || typeof val !== "string" || !val.trim()) continue;

                const trimmedVal = val.trim();

                // 检查是否为有效的邮箱地址或邮箱分组
                if (isEmailAddress(trimmedVal) || isEmailGroup(trimmedVal)) {
                    validatedValues.push(trimmedVal);
                } else if (allowCustomInput) {
                    // 如果允许自定义输入，也接受非邮箱格式的值
                    validatedValues.push(trimmedVal);
                } else {
                    message.warning(`"${trimmedVal}" 不是有效的邮箱地址或分组`);
                }
            }

            onChange(validatedValues);
        },
        [onChange, isEmailGroup, allowCustomInput],
    );

    // 清空所有选择
    const handleClearAll = useCallback(() => {
        onChange([]);
    }, [onChange]);

    // 异步加载分组详情的标签组件
    const AsyncGroupTag = ({ label, value, closable, onClose, initialGroupInfo }) => {
        const [groupDetail, setGroupDetail] = useState(initialGroupInfo);
        const [loading, setLoading] = useState(false);
        const groupName = value;

        // 监听 initialGroupInfo 变化
        useEffect(() => {
            if (initialGroupInfo) {
                setGroupDetail(initialGroupInfo);
            }
        }, [initialGroupInfo]);

        const handleOpenChange = async (visible) => {
            // 如果可见，且没有成员信息，且没有正在加载，且有ID，则加载详情
            if (visible && !groupDetail?.members && !loading && groupDetail?.id) {
                setLoading(true);
                try {
                    // 使用专门的接口获取分组下的所有邮箱地址
                    const response = await masterDataService.emailAddress.getEmailAddressesByGroup(groupDetail.id);
                    // 兼容处理：检查响应格式
                    const members = Array.isArray(response) ? response : response.data || response.items || [];

                    setGroupDetail((prev) => ({ ...prev, members }));
                } catch (error) {
                    console.error("加载分组详情失败:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        const tooltip = (
            <div style={{ padding: 4, minWidth: 200, maxWidth: 350 }}>
                {/* 头部：分组信息 */}
                {groupDetail && (
                    <div style={{ paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.2)", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontWeight: 600, fontSize: "14px", display: "flex", alignItems: "center" }}>
                                <TeamOutlined style={{ marginRight: 6 }} />
                                {groupName}
                            </span>
                            {groupDetail.members && <Tag style={{ margin: 0, fontSize: 10, lineHeight: "16px", background: "rgba(255,255,255,0.1)", border: "none", color: "inherit" }}>{groupDetail.members.length} 人</Tag>}
                        </div>
                        {groupDetail.description && <div style={{ opacity: 0.75, fontSize: 12 }}>{groupDetail.description}</div>}
                    </div>
                )}

                <div style={{ fontSize: 12 }}>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "8px 0" }}>
                            <Spin size="small" /> 加载成员中...
                        </div>
                    ) : groupDetail?.members?.length > 0 ? (
                        <div style={{ maxHeight: 250, overflowY: "auto" }}>
                            {groupDetail.members.map((member, i) => (
                                <div key={member.id || i} style={{ padding: "4px 0", borderBottom: i < groupDetail.members.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none", display: "flex", flexDirection: "column" }}>
                                    <div style={{ fontWeight: 500 }}>{member.display_name}</div>
                                    <div style={{ opacity: 0.65 }}>{member.email}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ opacity: 0.5, textAlign: "center", padding: "8px 0" }}>该分组下暂无成员</div>
                    )}
                </div>
            </div>
        );

        return (
            <Tooltip title={tooltip} overlayStyle={{ maxWidth: 350 }} onOpenChange={handleOpenChange}>
                <Tag color="blue" closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }} icon={<TeamOutlined />}>
                    {value}
                </Tag>
            </Tooltip>
        );
    };

    // ... inside RecipientSelector ...

    // 渲染标签
    const tagRender = useCallback(
        (props) => {
            const { value: tagValue, closable, onClose } = props;

            if (!tagValue || typeof tagValue !== "string") {
                return null;
            }

            // 分组标签使用异步组件
            if (isEmailGroup(tagValue)) {
                const groupInfo = emailGroups.find((g) => g.name === tagValue);
                // 传 label=null 因为内部现在使用 value (groupName)
                return <AsyncGroupTag label={null} value={tagValue} closable={closable && !disabled} onClose={onClose} initialGroupInfo={groupInfo} />;
            }

            // 其他类型 - 计算简单的显示文本，避免使用复杂的 props.label
            let icon, color, tooltip, simpleLabel;

            if (isEmailAddress(tagValue)) {
                // 用户要求：直接显示整个邮箱地址，不用显示名称了
                simpleLabel = tagValue;

                const addressInfo = emailAddresses.find((a) => a.email === tagValue);

                if (isExistingEmailAddress(tagValue)) {
                    icon = <MailOutlined />;
                    color = "green";
                    tooltip = (
                        <div>
                            <div>
                                <strong>{addressInfo?.display_name}</strong>
                            </div>
                            <div>{tagValue}</div>
                            {addressInfo?.description && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{addressInfo.description}</div>}
                        </div>
                    );
                } else {
                    icon = <MailOutlined />;
                    color = "default";
                    tooltip = `邮箱地址: ${tagValue}`;
                }
            } else {
                icon = <QuestionCircleOutlined />;
                color = "orange";
                tooltip = `自定义: ${tagValue}`;
                simpleLabel = tagValue;
            }

            return (
                <Tooltip title={tooltip} overlayStyle={{ maxWidth: 300 }}>
                    <Tag color={color} closable={closable && !disabled} onClose={onClose} style={{ marginInlineEnd: 4 }} icon={icon}>
                        {simpleLabel}
                    </Tag>
                </Tooltip>
            );
        },
        [isEmailGroup, isExistingEmailAddress, disabled, emailGroups, emailAddresses],
    );

    // 大小写不敏感的字母排序：缺失值排到末尾，保证空值健壮
    const sortByFieldAlphabetically = (list, field) => {
        return [...list].sort((a, b) => {
            const va = (a?.[field] || "").toString().toLowerCase();
            const vb = (b?.[field] || "").toString().toLowerCase();
            // 缺失值（空串）排到末尾：a 空 b 非空时 a 后；b 空 a 非空时 b 后
            if (!va && !vb) return 0;
            if (!va) return 1;
            if (!vb) return -1;
            return va.localeCompare(vb);
        });
    };

    // 准备选项：分组段在前、地址段在后，两段各自按字母升序排列
    const options = [
        // 邮箱分组选项（按 name 字母序）
        ...(allowGroups
            ? sortByFieldAlphabetically(emailGroups, "name").map((group) => {
                  const previewText = group.preview_emails && group.preview_emails.length > 0 ? group.preview_emails.join(", ") + (group.email_count > group.preview_emails.length ? "..." : "") : group.description;

                  return {
                      label: (
                          <GroupInfoTooltip groupName={group.name} initialGroupInfo={group} placement="right">
                              <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                  <TeamOutlined style={{ color: "#1890ff", flexShrink: 0, marginRight: 8, fontSize: 16 }} />
                                  <span style={{ fontWeight: 500, flexShrink: 0 }}>{group.name}</span>
                                  <Tag color="blue" bordered={false} style={{ marginLeft: 8, fontSize: 10, lineHeight: "16px", height: 18, padding: "0 4px", flexShrink: 0 }}>
                                      {(group.email_count !== undefined ? group.email_count : group.members?.length) || 0} 人
                                  </Tag>
                                  {previewText && (
                                      <span
                                          style={{
                                              color: "#999",
                                              fontSize: 12,
                                              marginLeft: 8,
                                              flex: 1,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                          }}
                                          title={previewText}
                                      >
                                          {previewText}
                                      </span>
                                  )}
                              </div>
                          </GroupInfoTooltip>
                      ),
                      value: group.name,
                      desc: group.description, // 用于搜索过滤
                      searchLabel: group.name, // 专门用于搜索的文本
                      type: "group",
                  };
              })
            : []),
        // 邮箱地址选项（按 email 字母序）
        ...(allowAddresses
            ? sortByFieldAlphabetically(emailAddresses, "email").map((address) => {
                  const displayName = address.display_name || address.email.split("@")[0];
                  return {
                      label: (
                          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                              <MailOutlined style={{ color: "#52c41a", flexShrink: 0, marginRight: 8 }} />
                              <span style={{ fontWeight: 500, flexShrink: 0 }}>{displayName}</span>
                              <span style={{ color: "#999", fontSize: 12, marginLeft: 8, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={address.email}>
                                  &lt;{address.email}&gt;
                              </span>
                          </div>
                      ),
                      value: address.email,
                      desc: address.description,
                      searchLabel: `${address.display_name || ""} ${address.email}`, // 专门用于搜索的文本
                      type: "address",
                  };
              })
            : []),
    ];

    // 自定义下拉渲染
    const popupRender = useCallback(
        (menu) => (
            <div>
                {menu}
                <div
                    style={{
                        padding: "8px 12px",
                        borderTop: "1px solid #f0f0f0",
                        color: "#999",
                        fontSize: "12px",
                        background: "#fafafa",
                    }}
                >
                    <Space direction="vertical" size={2}>
                        {allowCustomInput && (
                            <Space>
                                <MailOutlined style={{ color: "#d9d9d9" }} />
                                <span>灰色标签为手动输入邮箱地址</span>
                            </Space>
                        )}
                        <Space>
                            <QuestionCircleOutlined style={{ color: "#ff4d4f" }} />
                            <span>橙色标签为无法识别地址</span>
                        </Space>
                        <Button type="text" size="small" icon={<ClearOutlined />} onClick={handleClearAll} style={{ width: "100%", textAlign: "left" }}>
                            清空所有选择
                        </Button>
                    </Space>
                </div>
            </div>
        ),
        [allowGroups, allowAddresses, allowCustomInput, handleClearAll],
    );

    return (
        <>
            <style>
                {`
                /* 专门解决：已选中项在鼠标悬浮时颜色没有加深的问题 */
                .recipient-selector-dropdown .ant-select-item-option-selected:hover,
                .recipient-selector-dropdown .ant-select-item-option-selected.ant-select-item-option-active {
                    background-color: #bae7ff !important; /* 使用比默认选中色更深的蓝色，确保悬浮可见 */
                }
                
                /* 确保内容区域不遮挡背景 */
                .recipient-selector-dropdown .ant-select-item-option-content > div {
                    pointer-events: none; 
                }
                /* 保持之前的微动画：当鼠标悬浮在选项上时，图标微放大 */
                .recipient-selector-dropdown .ant-select-item-option-active .anticon {
                    transform: scale(1.1);
                    transition: transform 0.2s;
                }
                `}
            </style>
            <Select
                mode="tags"
                style={{
                    ...style,
                }}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                options={options}
                loading={loading}
                disabled={disabled}
                allowClear={allowClear}
                tagRender={tagRender}
                popupRender={popupRender}
                popupClassName="recipient-selector-dropdown"
                dropdownClassName="recipient-selector-dropdown"
                listHeight={400}
                filterOption={(input, option) => {
                    if (!input) return true;
                    const searchText = input.toLowerCase();
                    // 优先匹配 searchLabel，如果没有则回退到 value（虽然 value 总是有的）
                    const matchText = (option.searchLabel || option.value || "").toLowerCase();
                    const descText = (option.desc || "").toLowerCase();
                    return matchText.includes(searchText) || descText.includes(searchText);
                }}
                notFoundContent={loading ? "加载中..." : "输入邮箱地址或选择分组/邮箱"}
                tokenSeparators={[",", ";", " "]} // 支持多种分隔符
                showSearch={true} // 启用搜索，增加输入体验
            />
        </>
    );
};

export default RecipientSelector;
